/**
 * ProfitTargetEditor — inline widget rendered inside the Profit Target
 * card on the Dashboard. Lets the user customise their profit target as a
 * percentage of starting balance (1% – 50%). Applies to ALL tiers.
 *
 * Persists via PATCH /api/accounts/:id/profit-target. On success we call
 * the parent's `onSaved()` so metrics refresh with the new target.
 */
import { useState } from "react";
import { Pencil, Check, X } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function ProfitTargetEditor({ accountId, currentPct, onSaved }) {
    const [editing, setEditing] = useState(false);
    const [pct, setPct]         = useState(Number(currentPct || 8).toFixed(1));
    const [busy, setBusy]       = useState(false);

    async function save() {
        const parsed = Number(pct);
        if (!Number.isFinite(parsed) || parsed < 1 || parsed > 50) {
            toast.error("Enter a profit target between 1% and 50%.");
            return;
        }
        setBusy(true);
        try {
            await api.patch(`/accounts/${accountId}/profit-target`, {
                profit_target_pct: parsed,
            });
            toast.success(`Profit target set to ${parsed}%`);
            setEditing(false);
            if (onSaved) onSaved(parsed);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't save profit target");
        } finally { setBusy(false); }
    }

    if (!editing) {
        return (
            <button
                type="button"
                onClick={() => setEditing(true)}
                data-testid="profit-target-edit-btn"
                className="ml-2 text-[10px] uppercase tracking-widest font-extrabold text-primary hover:text-primary/80 inline-flex items-center gap-1"
                title="Change your profit target"
            >
                <Pencil className="w-3 h-3" /> Edit
            </button>
        );
    }
    return (
        <div className="ml-2 inline-flex items-center gap-1" data-testid="profit-target-editor">
            <input
                type="number"
                value={pct}
                onChange={(e) => setPct(e.target.value)}
                min="1" max="50" step="0.5"
                className="w-14 h-6 text-xs mono border border-border rounded px-1 bg-background"
                data-testid="profit-target-pct-input"
            />
            <span className="text-[10px] text-muted-foreground">%</span>
            <button onClick={save} disabled={busy} className="text-emerald-600 dark:text-emerald-400 disabled:opacity-50" data-testid="profit-target-save">
                <Check className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => setEditing(false)} className="text-muted-foreground hover:text-foreground" data-testid="profit-target-cancel">
                <X className="w-3.5 h-3.5" />
            </button>
        </div>
    );
}
